"use client";

import { useMemo, useState } from "react";
import { ArrowLeft, Copy, Eye, Plus, Trash2, Save } from "lucide-react";
import { test, test1, test2, test3, test4 } from "@/assets";
import Image from "next/image";
type SequenceBuilderProps = {
  sequenceName: string;
  onBack: () => void;
};

const DEFAULT_POOL = [
  "ป1 พยัญชนะที่ ห นำได้มีกี่ตัว",
  "ป1 จัดหมวด ห นำ",
  "ป1 จับคู่คำศัพท์ ห นำ",
  "ป1 จับคู่พยัญชนะ ห นำ",
  "ป1 จัดหมวดคำตัวสะกด",
  "ป1 จัดหมวดคำแม่ ก กา",
];

type DraggingState = {
  item: string;
  source: "pool" | "selected";
};

export default function SequenceBuilder({ sequenceName, onBack }: SequenceBuilderProps) {
  const [poolItems, setPoolItems] = useState<string[]>(DEFAULT_POOL);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [draggingState, setDraggingState] = useState<DraggingState | null>(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);

  const canDropToSelected = useMemo(() => draggingState?.source === "pool", [draggingState]);
  const canDropToPool = useMemo(() => draggingState?.source === "selected", [draggingState]);

  const handleDragStart = (item: string, source: "pool" | "selected") => {
    setDraggingState({ item, source });
  };

  const handleDropToSelected = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggingState || draggingState.source === "selected") return;

    setSelectedItems((prev) => [...prev, draggingState.item]);
    setPoolItems((prev) => prev.filter((item) => item !== draggingState.item));
    setDraggingState(null);
  };

  const handleDropToPool = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggingState || draggingState.source === "pool") return;

    setPoolItems((prev) => [...prev, draggingState.item]);
    setSelectedItems((prev) => prev.filter((item) => item !== draggingState.item));
    setDraggingState(null);
  };

  const handleDeleteFromSelected = (item: string) => {
    setPoolItems((prev) => [...prev, item]);
    setSelectedItems((prev) => prev.filter((i) => i !== item));
  };

  const handleSave = () => {
    setShowSaveConfirm(true);
  };

  const handleConfirmSave = () => {
    setShowSaveConfirm(false);
    // Immediately go back without showing success message
    onBack();
  };

  const handleCancelSave = () => {
    setShowSaveConfirm(false);
  };

  // Fake thumbnails for items mapped to DEFAULT_POOL index
  const getImage = (name: string) => {
    const index = DEFAULT_POOL.indexOf(name);
    const images = [test, test1, test2, test3, test4];
    if (index >= 0 && index < images.length) {
      return images[index];
    }
    return null;
  };

  return (
    <div className="flex h-full w-full flex-col font-sans">
      <div className="flex flex-1 flex-col gap-6 lg:flex-row">
        {/* Left Column - Sequence Items */}
        <div className="flex w-full flex-col gap-4 lg:w-[45%]">
          {/* Sequence Name Box */}
          <div className="rounded-xl bg-white p-5 shadow-sm">
            <h2 className="mb-2 text-sm font-bold text-[#5c5c5c]">Sequence Name</h2>
            <div className="flex gap-3">
              <div className="flex-1 rounded-lg bg-[#d9d9d9] px-4 py-3 text-sm font-bold text-[#5c5c5c] select-none text-left">
                {sequenceName}
              </div>
              <button className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-[#FDEF5C] px-5 py-3 text-sm font-bold text-[#2B2B2B] transition-colors hover:bg-[#f3e54b]">
                <Eye className="size-4" /> ดูตัวอย่าง
              </button>
            </div>
          </div>

          {/* Droppable Area for Selected Items */}
          <div
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleDropToSelected}
            className={`flex-1 overflow-y-auto rounded-xl shadow-sm transition-colors p-5 ${canDropToSelected ? "border-2 border-[#0092D7] bg-[#f0f9ff]" : "bg-white"
              }`}
          >
            {selectedItems.length === 0 ? (
              <div className="flex h-40 items-center justify-center rounded-xl border-2 border-dashed border-[#d2d2d2]">
                <p className="text-sm font-medium text-[#757575]">ลากรายการจากฝั่งขวาเข้ามาวางที่นี่</p>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedItems.map((item) => (
                  <div
                    key={item}
                    draggable
                    onDragStart={() => handleDragStart(item, "selected")}
                    onDragEnd={() => setDraggingState(null)}
                    className="flex cursor-grab items-center justify-between rounded-xl bg-[#eeeeee] px-4 py-3 shadow-sm hover:bg-[#e5e5e5] active:cursor-grabbing"
                  >
                    <span className="text-[14px] font-bold text-[#5c5c5c]">{item}</span>
                    <div className="flex items-center gap-4">
                      {/* Fake Thumbnail */}
                      <div className={`relative h-[28px] w-[50px] shrink-0 overflow-hidden rounded-[4px] ${!getImage(item) ? 'bg-[#d9d9d9]' : ''}`}>
                        {getImage(item) && (
                          <Image src={getImage(item)!} alt="thumbnail" fill sizes="50px" className="object-cover" />
                        )}
                      </div>
                      <button
                        className="text-[#4b4b4b] transition-colors hover:text-[#000]"
                        onClick={() => handleDeleteFromSelected(item)}
                      >
                        <Trash2 className="size-[18px]" strokeWidth={2.5} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Buttons at the bottom */}
            <div className="flex justify-center items-center gap-2 mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={onBack}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
              >
                <ArrowLeft className="size-4" />
                Back
              </button>
              <button
                onClick={handleSave}
                className="inline-flex items-center gap-2 rounded-lg bg-green-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-600"
              >
                <Save className="size-4" />
                บันทึก
              </button>
            </div>
          </div>
        </div>

        {/* Right Column - Available Games (Pool) */}
        <div
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleDropToPool}
          className={`flex-1 overflow-y-auto rounded-xl p-6 shadow-sm transition-colors ${canDropToPool ? "border-2 border-[#0092D7] bg-[#f0f9ff]" : "bg-white"
            }`}
        >
          <div className="mb-8 flex justify-end">
            <button className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#0092D7] px-6 py-2.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-[#007fc0]">
              <Plus className="size-4" strokeWidth={2.5} /> เพิ่มโดยรหัส
            </button>
          </div>

          <div className="space-y-3">
            {poolItems.map((item) => (
              <div
                key={item}
                draggable
                onDragStart={() => handleDragStart(item, "pool")}
                onDragEnd={() => setDraggingState(null)}
                className="flex cursor-grab items-center justify-between rounded-xl bg-[#eeeeee] px-4 py-3 shadow-sm hover:bg-[#e5e5e5] active:cursor-grabbing"
              >
                <span className="text-[14px] font-bold text-[#5c5c5c]">{item}</span>
                <div className="flex items-center gap-4">
                  {/* Fake Thumbnail */}
                  <div className={`relative h-[28px] w-[50px] shrink-0 overflow-hidden rounded-[4px] ${!getImage(item) ? 'bg-[#d9d9d9]' : ''}`}>
                    {getImage(item) && (
                      <Image src={getImage(item)!} alt="thumbnail" fill sizes="50px" className="object-cover" />
                    )}
                  </div>
                  <button className="text-[#4b4b4b] transition-colors hover:text-[#000]">
                    <Copy className="size-[18px]" strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            ))}
            {poolItems.length === 0 && (
              <div className="flex h-32 items-center justify-center rounded-xl border-2 border-dashed border-[#d2d2d2]">
                <p className="text-sm font-medium text-[#757575]">ไม่มีรายการเหลือสำหรับลากแล้ว</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save Confirmation Modal */}
      {showSaveConfirm && (
        <div className="fixed inset-0 flex items-center justify-center z-50 ">
          <div className="bg-white/90 backdrop-blur-sm rounded-xl p-6 shadow-lg max-w-xs w-full mx-4">
            <div className="text-center">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Save className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">ยืนยันการบันทึก</h3>
              <p className="text-sm text-gray-600 mb-6">ต้องการบันทึกข้อมูลนี้ใช่หรือไม่?</p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={handleCancelSave}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  ยกเลิก
                </button>
                <button
                  onClick={handleConfirmSave}
                  className="px-6 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors"
                >
                  ตกลง
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

